import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../../lib/api";
import { gbp } from "../../context/CartContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Bike, ShoppingBag, Phone, Clock, Volume2, VolumeX, Flame, RotateCcw } from "lucide-react";

const COLUMNS = [
  { key: "new", title: "NEW", tone: "bg-amber-100 border-amber-300 text-amber-900" },
  { key: "preparing", title: "PREPARING", tone: "bg-orange-100 border-orange-300 text-orange-900" },
  { key: "ready", title: "READY / OUT", tone: "bg-green-100 border-green-300 text-green-900" },
  { key: "completed", title: "COMPLETED (2h)", tone: "bg-gray-100 border-gray-300 text-gray-700" },
];

const NEXT = {
  new: { label: "Accept & Start Preparing", to: "preparing" },
  preparing: { label: "Mark Ready", to: "ready" },
};

export default function LiveOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const seenNew = useRef(new Set());
  const first = useRef(true);
  const soundRef = useRef(true);
  const audioCtxRef = useRef(null);

  const getCtx = () => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
    }
    if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    return audioCtxRef.current;
  };

  // Play a loud two-tone chime, repeated so it cuts through a noisy bar.
  const chime = (repeats = 3) => {
    const ctx = getCtx();
    if (!ctx) return;
    let t = ctx.currentTime + 0.02;
    for (let i = 0; i < repeats; i++) {
      [880, 1245].forEach((freq, j) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = freq;
        const start = t + j * 0.16;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.7, start + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, start + 0.32);
        o.start(start); o.stop(start + 0.34);
      });
      t += 0.75;
    }
  };

  const enableAudio = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === "running") setAudioReady(true);
  };
  const [busy, setBusy] = useState(false);
  const [busyMins, setBusyMins] = useState(15);
  const [busyDialog, setBusyDialog] = useState(false);
  const [minsInput, setMinsInput] = useState("15");
  const [deliveryPaused, setDeliveryPaused] = useState(false);

  const loadBusy = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setBusy(!!data.busy_mode);
      setBusyMins(Number(data.busy_extra_mins) || 15);
      setDeliveryPaused(!!data.delivery_paused);
    } catch {}
  }, []);

  const toggleDelivery = () => {
    const n = !deliveryPaused;
    setDeliveryPaused(n);
    api.put("/admin/settings", { delivery_paused: n }).catch(() => setDeliveryPaused(!n));
  };

  const clickBusy = () => {
    if (busy) {
      setBusy(false);
      api.put("/admin/settings", { busy_mode: false }).catch(() => setBusy(true));
    } else {
      setMinsInput(String(busyMins || 15));
      setBusyDialog(true);
    }
  };

  const confirmBusy = async () => {
    const mins = Math.max(1, parseInt(minsInput, 10) || 15);
    setBusy(true); setBusyMins(mins); setBusyDialog(false);
    try { await api.put("/admin/settings", { busy_mode: true, busy_extra_mins: mins }); }
    catch { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      const currentNew = data.filter((o) => o.status === "new").map((o) => o.id);
      if (!first.current) {
        const fresh = currentNew.filter((id) => !seenNew.current.has(id));
        if (fresh.length > 0 && soundRef.current) chime();
      }
      seenNew.current = new Set(currentNew);
      first.current = false;
      setOrders(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    loadBusy();
    const t = setInterval(load, 8000);
    // Unlock audio on the first interaction anywhere on the board (browser autoplay policy).
    const unlock = () => { enableAudio(); };
    window.addEventListener("pointerdown", unlock);
    return () => { clearInterval(t); window.removeEventListener("pointerdown", unlock); };
  }, [load, loadBusy]);

  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);

  const grouped = (key) => {
    if (key === "ready") return orders.filter((o) => o.status === "ready" || o.status === "out_for_delivery");
    if (key === "completed") {
      // Only show recently-completed orders so the board never piles up (and clears overnight).
      const cutoff = Date.now() - 120 * 60000; // 2 hours
      return orders.filter((o) => o.status === "completed" && o.updated_at && new Date(o.updated_at).getTime() >= cutoff);
    }
    return orders.filter((o) => o.status === key);
  };

  const advance = async (order, toStatus) => {
    await api.patch(`/admin/orders/${order.id}/status`, { status: toStatus });
    await load();
    setSelected(null);
  };

  const PREV = { preparing: "new", ready: "preparing", out_for_delivery: "ready" };
  const prevStatus = (o) =>
    o.status === "completed" ? (o.type === "delivery" ? "out_for_delivery" : "ready") : PREV[o.status];

  const moveBack = async (order) => {
    const to = prevStatus(order);
    if (!to) return;
    if (!window.confirm(`Move order #${order.order_number} back a step?`)) return;
    await api.patch(`/admin/orders/${order.id}/status`, { status: to });
    await load();
    setSelected(null);
  };

  const actionsFor = (order) => {
    if (order.status === "new") return [{ label: "ACCEPT / START PREPARING", to: "preparing", cls: "bg-orange-500 hover:bg-orange-600" }];
    if (order.status === "preparing") return [{ label: "MARK READY", to: "ready", cls: "bg-green-600 hover:bg-green-700" }];
    if (order.status === "ready" && order.type === "delivery") return [{ label: "OUT FOR DELIVERY", to: "out_for_delivery", cls: "bg-brand-teal hover:bg-brand-teal-dark" }];
    if (order.status === "ready" || order.status === "out_for_delivery") return [{ label: "COMPLETE ORDER", to: "completed", cls: "bg-brand-dark hover:bg-brand-dark-2" }];
    return [];
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-6 py-4">
        <h1 className="font-head text-2xl md:text-3xl font-bold text-brand-dark">Live Orders</h1>
        <div className="flex items-center gap-2">
          <button data-testid="delivery-pause-toggle" onClick={toggleDelivery}
            className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${deliveryPaused ? "bg-amber-500 text-white" : "bg-brand-dark/10 text-brand-dark/60"}`}>
            <Bike className="w-4 h-4" /> {deliveryPaused ? "Delivery OFF" : "Delivery on"}
          </button>
          <button data-testid="busy-toggle" onClick={clickBusy}
            className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${busy ? "bg-orange-500 text-white" : "bg-brand-dark/10 text-brand-dark/60"}`}>
            <Flame className="w-4 h-4" /> {busy ? `Busy mode ON (+${busyMins} min)` : "Busy mode"}
          </button>
          <button data-testid="sound-toggle" onClick={() => { const n = !soundOn; setSoundOn(n); if (n) { enableAudio(); chime(1); } }}
            className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold ${soundOn ? "bg-brand-teal text-white" : "bg-brand-dark/10 text-brand-dark/60"}`}>
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} {soundOn ? "Alerts on" : "Alerts off"}
          </button>
        </div>
      </div>

      {soundOn && !audioReady && (
        <div className="mx-4 md:mx-6 mb-3 rounded-xl bg-amber-100 border border-amber-300 px-4 py-3 flex items-center justify-between gap-3" data-testid="enable-sound-banner">
          <p className="text-sm text-amber-900 font-medium">Tap to enable new-order sound alerts on this device.</p>
          <button data-testid="enable-sound-btn" onClick={() => { enableAudio(); chime(1); }}
            className="shrink-0 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold">
            Enable sound
          </button>
        </div>
      )}

      <div className="flex-1 overflow-x-auto px-4 md:px-6 pb-6">
        <div className="flex gap-4 min-w-max h-full">
          {COLUMNS.map((col) => {
            const list = grouped(col.key);
            return (
              <div key={col.key} className="w-[300px] flex flex-col" data-testid={`col-${col.key}`}>
                <div className={`rounded-xl border px-3 py-2 font-bold text-sm flex items-center justify-between ${col.tone}`}>
                  <span>{col.title}</span>
                  <span className="bg-white/60 rounded-full px-2 text-xs">{list.length}</span>
                </div>
                <div className="mt-3 space-y-3 overflow-y-auto no-scrollbar">
                  {list.map((o) => (
                    <button key={o.id} data-testid={`order-card-${o.order_number}`} onClick={() => setSelected(o)}
                      className={`w-full text-left bg-white rounded-2xl p-3 border shadow-sm active:scale-[0.99] transition-transform ${o.status === "new" ? "border-amber-400 animate-pulse-ring" : "border-brand-dark/10"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-head font-bold text-lg text-brand-dark">#{o.order_number}</span>
                        <Badge className={`border-0 text-white ${o.type === "delivery" ? "bg-brand-teal" : "bg-brand-dark"}`}>
                          {o.type === "delivery" ? <Bike className="w-3 h-3 mr-1" /> : <ShoppingBag className="w-3 h-3 mr-1" />}{o.type}
                        </Badge>
                      </div>
                      <p className="text-sm text-brand-dark/70 mt-1">{o.customer?.first_name} {o.customer?.last_name}</p>
                      <p className="text-xs text-brand-dark/40">{o.items?.reduce((s, i) => s + i.qty, 0)} items · {gbp(o.total)}</p>
                      {o.status === "out_for_delivery" && <Badge variant="outline" className="mt-1 text-[10px] border-brand-teal text-brand-teal">Out for delivery</Badge>}
                    </button>
                  ))}
                  {list.length === 0 && <p className="text-center text-xs text-brand-dark/30 py-6">No orders</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head text-2xl flex items-center justify-between">
                  <span>Order #{selected.order_number}</span>
                  <Badge className={`border-0 text-white ${selected.type === "delivery" ? "bg-brand-teal" : "bg-brand-dark"}`}>{selected.type}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                <div className="bg-brand-cream rounded-xl p-3">
                  <p className="font-semibold text-brand-dark">{selected.customer?.first_name} {selected.customer?.last_name}</p>
                  <p className="text-sm text-brand-dark/60 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {selected.customer?.phone}</p>
                  {selected.type === "delivery" && selected.address && (
                    <p className="text-sm text-brand-dark/60 mt-1">{selected.address.line1}{selected.address.line2 ? `, ${selected.address.line2}` : ""}, {selected.address.town}, {selected.address.postcode}</p>
                  )}
                  <p className="text-sm text-brand-dark/60 flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" /> {selected.order_time_type === "asap" ? "ASAP" : `Scheduled ${selected.scheduled_time}`} · Est. {selected.estimated_time}</p>
                  {selected.delivery_instructions && <p className="text-sm text-brand-teal-dark bg-brand-teal/10 rounded-lg p-2 mt-2">🛈 {selected.delivery_instructions}</p>}
                </div>
                <div className="space-y-2">
                  {selected.items?.map((it, i) => (
                    <div key={i} className="border-b pb-2">
                      <div className="flex justify-between font-semibold text-brand-dark"><span>{it.qty}× {it.name}</span><span>{gbp(it.line_total)}</span></div>
                      {it.extras?.map((e) => <p key={e.id} className="text-xs text-brand-dark/50 ml-2">+ {e.name}</p>)}
                      {it.notes && <p className="text-sm text-red-600 font-medium ml-2 mt-0.5">Note: {it.notes}</p>}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-bold text-lg"><span>Amount paid</span><span>{gbp(selected.total)}</span></div>
              </div>
              <div className="space-y-2 pt-2">
                {actionsFor(selected).map((a) => (
                  <Button key={a.to} data-testid={`advance-${a.to}`} onClick={() => advance(selected, a.to)}
                    className={`w-full h-14 rounded-2xl text-white text-base font-bold ${a.cls}`}>{a.label}</Button>
                ))}
                {selected.status !== "new" && (
                  <Button variant="outline" data-testid="move-back-btn" onClick={() => moveBack(selected)}
                    className="w-full h-11 rounded-2xl border-brand-dark/20 text-brand-dark/70 font-semibold hover:bg-brand-dark/5">
                    <RotateCcw className="w-4 h-4 mr-1" /> Undo — move back a step
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={busyDialog} onOpenChange={setBusyDialog}>
        <DialogContent className="max-w-xs" data-testid="busy-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><Flame className="w-5 h-5 text-orange-500" /> Busy mode</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-dark/60">How many extra minutes should we add to every customer estimate?</p>
          <div className="grid grid-cols-4 gap-2 my-1">
            {[10, 15, 20, 30].map((m) => (
              <button key={m} data-testid={`busy-preset-${m}`} onClick={() => setMinsInput(String(m))}
                className={`py-2 rounded-xl text-sm font-semibold border ${minsInput === String(m) ? "bg-orange-500 text-white border-orange-500" : "border-brand-dark/15 text-brand-dark/60"}`}>+{m}</button>
            ))}
          </div>
          <div>
            <label className="text-xs text-brand-dark/50">Or enter a custom amount (minutes)</label>
            <Input data-testid="busy-mins-input" type="number" min="1" value={minsInput} onChange={(e) => setMinsInput(e.target.value)} className="rounded-xl mt-1" />
          </div>
          <Button data-testid="busy-confirm" onClick={confirmBusy} className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold mt-2">
            Turn on busy mode
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
